import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtHora } from "@/lib/datetime";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import {
  UsersThree,
  CheckCircle,
  Clock,
  Warning,
  Calendar,
  TrendUp,
  Buildings,
  ChartLineUp,
  Coffee,
  UserCircle,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Line,
  LineChart,
} from "recharts";
import GuidedTour from "@/components/GuidedTour";
import TourHelpButton from "@/components/TourHelpButton";
import { useGuidedTour } from "@/hooks/useGuidedTour";

const TOUR_STORAGE_KEY = "chkr_tour_dashboard_seen";

const TOUR_STEPS = [
  { target: "[data-tour='kpis-hoy']", title: "Indicadores del día", content: "Empleados activos, presentes, ausentes, retardos y omisiones de salida — el pulso operativo de hoy." },
  { target: "[data-tour='en-vivo']", title: "Quién está en jornada ahora", content: "Vista en vivo de quién está trabajando, quién está en descanso y quién aún no marca entrada. Se actualiza sola cada 30 segundos." },
  { target: "[data-tour='kpis-periodo']", title: "Pendientes por revisar", content: "Horas extra acumuladas y las incidencias o correcciones que aún esperan tu autorización." },
  { target: "[data-tour='graficas']", title: "Tendencias de 7 días", content: "Compara presentes contra retardos y da seguimiento a la acumulación de horas extra en la semana." },
  { target: "[data-tour='cumplimiento']", title: "Estado de cumplimiento", content: "Resumen de si tienes pendientes por revisar, conforme al Art. 132 fracción XXXIV de la LFT." },
];

const REFRESCO_EN_VIVO_MS = 30_000;

function ListaEnVivo({ items, emptyLabel, renderMeta }) {
  if (!items.length) {
    return <p className="text-sm text-slate-400 py-4 text-center">{emptyLabel}</p>;
  }
  return (
    <ul className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
      {items.map((p) => (
        <li key={p.empleado_id} className="flex items-center justify-between py-2.5 text-sm">
          <div className="min-w-0">
            <div className="font-semibold text-ink truncate">{p.nombre}</div>
            <div className="text-xs text-slate-500 truncate">{p.centro_nombre}{p.puesto ? ` · ${p.puesto}` : ""}</div>
          </div>
          <div className="font-mono text-xs text-slate-500 shrink-0 ml-3">{renderMeta(p)}</div>
        </li>
      ))}
    </ul>
  );
}

function Kpi({ label, value, icon: Icon, accent = "slate", testid, sub }) {
  return (
    <div className={`kpi-card kpi-card-accent-${accent}`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="label-uppercase">{label}</div>
          <div className="font-display text-3xl font-black mt-2 text-ink tracking-tight">{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <Icon size={26} weight="duotone" className="text-slate-400" />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [k, setK] = useState(null);
  const [serie, setSerie] = useState([]);
  const [enVivo, setEnVivo] = useState(null);
  const [loading, setLoading] = useState(true);
  const { active: tourActive, start: startTour, finish: finishTour } = useGuidedTour(TOUR_STORAGE_KEY, !loading && !!k);

  useEffect(() => {
    Promise.all([api.get("/dashboard/kpis"), api.get("/dashboard/asistencia-semana")])
      .then(([a, b]) => {
        setK(a.data);
        setSerie(b.data);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const cargarEnVivo = () => api.get("/dashboard/en-vivo").then((r) => setEnVivo(r.data)).catch(() => {});
    cargarEnVivo();
    const t = setInterval(cargarEnVivo, REFRESCO_EN_VIVO_MS);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="p-6 md:p-10" data-testid="dashboard-page">
      <PageHeader
        kicker="Centro de Control"
        title="Dashboard"
        subtitle={`Resumen ejecutivo de cumplimiento — ${new Date().toLocaleDateString("es-MX", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}`}
        actions={!loading && k && <TourHelpButton onClick={startTour} />}
      />

      <GuidedTour steps={TOUR_STEPS} active={tourActive} onFinish={finishTour} />

      {loading || !k ? (
        <div className="text-slate-500" data-testid="dashboard-loading">Cargando KPIs…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6" data-tour="kpis-hoy">
            <Kpi label="Empleados activos" value={k.empleados_activos} icon={UsersThree} accent="slate" testid="kpi-activos" sub={`de ${k.empleados_total} totales`} />
            <Kpi label="Presentes hoy" value={k.presentes_hoy} icon={CheckCircle} accent="emerald" testid="kpi-presentes" />
            <Kpi label="Ausentes hoy" value={k.ausentes_hoy} icon={Warning} accent="red" testid="kpi-ausentes" />
            <Kpi label="Retardos hoy" value={k.retardos_hoy} icon={Clock} accent="amber" testid="kpi-retardos" />
            <Kpi label="Omisiones de salida" value={k.omisiones_salida} icon={Calendar} accent="amber" testid="kpi-omisiones" />
          </div>

          <div className="bg-white border border-slate-200 rounded-md mb-8" data-tour="en-vivo" data-testid="panel-en-vivo">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                <h3 className="font-display font-bold text-ink">En este momento</h3>
              </div>
              <span className="text-xs text-slate-400">Se actualiza cada 30s</span>
            </div>
            {!enVivo ? (
              <div className="p-6 text-sm text-slate-400">Cargando…</div>
            ) : (
              <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-100">
                <div className="p-5">
                  <div className="label-uppercase mb-1 flex items-center gap-1.5 text-emerald-700">
                    <CheckCircle size={13} weight="fill" /> En jornada · {enVivo.en_jornada.length}
                  </div>
                  <ListaEnVivo items={enVivo.en_jornada} emptyLabel="Nadie en jornada ahora mismo" renderMeta={(p) => `desde ${fmtHora(p.hora_entrada, p.zona_horaria)}`} />
                </div>
                <div className="p-5">
                  <div className="label-uppercase mb-1 flex items-center gap-1.5 text-amber-700">
                    <Coffee size={13} weight="fill" /> En descanso · {enVivo.en_descanso.length}
                  </div>
                  <ListaEnVivo items={enVivo.en_descanso} emptyLabel="Nadie en descanso" renderMeta={(p) => `desde ${fmtHora(p.desde, p.zona_horaria)}`} />
                </div>
                <div className="p-5">
                  <div className="label-uppercase mb-1 flex items-center gap-1.5 text-slate-500">
                    <UserCircle size={13} weight="fill" /> Sin marcar entrada · {enVivo.sin_marcar.length}
                  </div>
                  <ListaEnVivo items={enVivo.sin_marcar} emptyLabel="Todos han marcado entrada" renderMeta={() => "—"} />
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-tour="kpis-periodo">
            <Kpi label="Horas Extra (7d)" value={`${k.horas_extra_semana}h`} icon={TrendUp} accent="emerald" testid="kpi-extra" />
            <Kpi label="Incidencias pendientes" value={k.incidencias_pendientes} icon={Warning} accent="amber" testid="kpi-incidencias" />
            <Kpi label="Correcciones pendientes" value={k.correcciones_pendientes} icon={Calendar} accent="amber" testid="kpi-correcciones" />
            <Kpi label="Centros activos" value={k.centros_activos} icon={Buildings} accent="slate" testid="kpi-centros" />
          </div>

          <div className="grid lg:grid-cols-2 gap-6" data-tour="graficas">
            <div className="bg-white border border-slate-200 rounded-md p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="label-uppercase">Asistencia · Últimos 7 días</div>
                  <h3 className="font-display text-xl font-bold mt-1 text-ink">Presentes vs Retardos</h3>
                </div>
                <ChartLineUp size={20} weight="duotone" className="text-slate-400" />
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={serie}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="fecha" stroke="#64748b" fontSize={11} tickFormatter={(d) => d.slice(5)} />
                  <YAxis stroke="#64748b" fontSize={11} />
                  <Tooltip
                    contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }}
                  />
                  <Bar dataKey="presentes" fill="#0f172a" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="retardos" fill="#f59e0b" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white border border-slate-200 rounded-md p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="label-uppercase">Horas Extra acumuladas</div>
                  <h3 className="font-display text-xl font-bold mt-1 text-ink">Tendencia 7 días</h3>
                </div>
                <TrendUp size={20} weight="duotone" className="text-emerald-500" />
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={serie}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="fecha" stroke="#64748b" fontSize={11} tickFormatter={(d) => d.slice(5)} />
                  <YAxis stroke="#64748b" fontSize={11} />
                  <Tooltip
                    contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }}
                  />
                  <Line type="monotone" dataKey="horas_extra" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3, fill: "#10b981" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="mt-8 bg-slate-50 border border-slate-200 rounded-md p-6" data-tour="cumplimiento">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <div className="label-uppercase">Estado de Cumplimiento</div>
                <h3 className="font-display text-2xl font-black mt-2 text-ink">
                  {k.incidencias_pendientes + k.correcciones_pendientes === 0 ? "Todo en orden" : `${k.incidencias_pendientes + k.correcciones_pendientes} pendientes por revisar`}
                </h3>
                <p className="text-slate-600 mt-1 text-sm">
                  Tu sistema está alineado al Art. 132 fracción XXXIV de la LFT. Todos los registros se conservan para fines probatorios.
                </p>
              </div>
              <div className="flex gap-6 px-6 py-3 bg-white border border-slate-200 rounded-md">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Sesión</div>
                  <div className="font-mono text-sm text-ink mt-0.5">{user?.email}</div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
